import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  ShieldAlert,
  UserPlus,
  UserRoundCog,
  Users,
  X,
} from 'lucide-react';

import PermissionGate from '../components/PermissionGate';
import {
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  MetricCard,
  PageHeader,
  SearchInput,
  SegmentedTabs,
  StatusPill,
  cx,
  formatDateTime,
} from '../components/EnterpriseAdminUI';
import { accessControlAPI } from '../services/api';

const FILTERS = [
  { id: 'all', label: 'All users' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'disabled', label: 'Disabled' },
];

function isMicrosoftLinked(user) {
  if (user.microsoft_linked || user.identity_provider === 'microsoft') return true;
  if (String(user.auth_method || '').toLowerCase().includes('microsoft')) return true;

  return (user.external_identities || []).some(
    (identity) => String(identity.provider || identity.provider_type || '').toLowerCase() === 'microsoft',
  );
}

function userState(user) {
  if (user.is_active) return 'active';

  const pendingInvitation = Boolean(
    user.pending_activation ||
      user.must_change_password ||
      (!isMicrosoftLinked(user) && !user.last_login && user.activation_required !== false),
  );

  return pendingInvitation ? 'pending' : 'disabled';
}

function stateBadge(user) {
  const state = userState(user);

  if (state === 'active') return <StatusPill label="Active" tone="success" />;
  if (state === 'pending') return <StatusPill label="Pending invitation" tone="warning" />;
  return <StatusPill label="Disabled" tone="danger" />;
}

function CreateAdminDialog({ open, groups, busy, onClose, onCreate }) {
  const [groupSearch, setGroupSearch] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', group_ids: [] });

  useEffect(() => {
    if (!open) {
      setGroupSearch('');
      setForm({ full_name: '', email: '', group_ids: [] });
    }
  }, [open]);

  if (!open) return null;

  const visibleGroups = groups.filter((group) =>
    [group.name, group.description]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(groupSearch.toLowerCase())),
  );

  const toggleGroup = (groupId) => {
    const id = String(groupId);
    setForm((previous) => ({
      ...previous,
      group_ids: previous.group_ids.includes(id)
        ? previous.group_ids.filter((item) => item !== id)
        : [...previous.group_ids, id],
    }));
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 px-4 py-8 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-labelledby="create-admin-title" className="max-h-full w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-600">Privileged account</p>
            <h2 id="create-admin-title" className="mt-1 text-xl font-black text-slate-950">Create local administrator</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              This creates a privileged local account and sends an activation email. Microsoft-synced non-admin users
              should be assigned from Groups & Permissions instead.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close create admin dialog" className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <InlineAlert tone="warning" title="Review before creating">
            The selected groups determine effective authorization. Avoid assigning Super Admin unless the user requires
            full organization control.
          </InlineAlert>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-bold text-slate-700">
              Full name
              <input
                value={form.full_name}
                onChange={(event) => setForm((previous) => ({ ...previous, full_name: event.target.value }))}
                placeholder="Administrator name"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </label>
            <label className="text-sm font-bold text-slate-700">
              Work email
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((previous) => ({ ...previous, email: event.target.value }))}
                placeholder="admin@example.com"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </label>
          </div>

          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-sm font-black text-slate-950">Initial access groups</h3>
                <p className="mt-1 text-xs text-slate-500">Groups can be changed later from Groups & Permissions.</p>
              </div>
              <div className="w-full sm:max-w-xs">
                <SearchInput
                  value={groupSearch}
                  onChange={setGroupSearch}
                  placeholder="Search groups"
                  ariaLabel="Search groups for new administrator"
                />
              </div>
            </div>

            <div className="mt-3 grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2">
              {visibleGroups.map((group) => {
                const selected = form.group_ids.includes(String(group.id));
                return (
                  <label
                    key={group.id}
                    className={cx(
                      'flex cursor-pointer items-start gap-3 rounded-2xl border p-3 transition',
                      selected ? 'border-orange-200 bg-orange-50' : 'border-slate-200 hover:bg-slate-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleGroup(group.id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span>
                      <span className="block text-sm font-black text-slate-950">{group.name}</span>
                      <span className="mt-1 line-clamp-2 block text-xs leading-5 text-slate-500">
                        {group.description || 'No description provided.'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onCreate({
              full_name: form.full_name.trim(),
              email: form.email.trim().toLowerCase(),
              group_ids: form.group_ids,
            })}
            disabled={busy || !form.full_name.trim() || !form.email.trim()}
            className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Create administrator
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadData = async ({ background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);

    setError('');

    try {
      const [usersRes, groupsRes] = await Promise.all([
        accessControlAPI.getAdminUsers(),
        accessControlAPI.getGroups(),
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load administrator accounts.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const counts = useMemo(() => {
    const next = { all: users.length, active: 0, pending: 0, disabled: 0 };
    users.forEach((user) => {
      next[userState(user)] += 1;
    });
    return next;
  }, [users]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return users.filter((user) => {
      if (filter !== 'all' && userState(user) !== filter) return false;
      if (!query) return true;

      return [user.full_name, user.email, user.role, ...(user.groups || []).map((group) => group.name)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [filter, search, users]);

  const createUser = async (payload) => {
    setCreating(true);
    setError('');
    setSuccess('');

    try {
      await accessControlAPI.createAdminUser(payload);
      setShowCreate(false);
      setSuccess('Administrator created and activation email sent.');
      await loadData({ background: true });
    } catch (err) {
      const payloadError = err?.response?.data || {};
      setError(
        payloadError.detail ||
          payloadError.email?.[0] ||
          payloadError.non_field_errors?.[0] ||
          err?.message ||
          'Failed to create administrator.',
      );
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async () => {
    if (!confirmAction?.user) return;

    const { user, nextActive } = confirmAction;
    setError('');
    setSuccess('');

    try {
      await accessControlAPI.updateAdminUserStatus(user.id, nextActive);
      setSuccess(nextActive ? 'Administrator reactivated.' : 'Administrator deactivated.');
      setSelectedUser(null);
      setConfirmAction(null);
      await loadData({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update administrator status.');
    }
  };

  const resendActivation = async (user) => {
    setError('');
    setSuccess('');

    try {
      await accessControlAPI.resendActivation(user.id);
      setSuccess(`Activation email sent to ${user.email}.`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to resend activation email.');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
          <span className="text-sm font-semibold text-slate-700">Loading administrator workspace…</span>
        </div>
      </div>
    );
  }

  return (
    <PermissionGate permission="manage_admin_users" showFallback>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Access governance"
          title="Administrator accounts"
          description="Manage privileged local administrators separately from ordinary Microsoft-synchronized users. Search, review access groups, and perform sensitive account actions from a deliberate user detail view."
          icon={UserRoundCog}
          actions={(
            <>
              <button
                type="button"
                onClick={() => loadData({ background: true })}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <RefreshCw className={cx('h-4 w-4', refreshing && 'animate-spin')} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
              >
                <UserPlus className="h-4 w-4" />
                Create administrator
              </button>
            </>
          )}
        />

        {error && <InlineAlert tone="danger" title="Administrator action failed">{error}</InlineAlert>}
        {success && <InlineAlert tone="success" title="Completed">{success}</InlineAlert>}

        <InlineAlert tone="info" title="Microsoft-synchronized users are not administrators by default">
          This workspace manages privileged local administrator accounts. Use Groups & Permissions to assign ordinary
          synchronized users to local authorization groups without promoting them to administrators.
        </InlineAlert>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Total administrators" value={counts.all} hint="Privileged local accounts" icon={Users} tone="brand" />
          <MetricCard label="Active" value={counts.active} hint="Currently permitted to authenticate" icon={CheckCircle2} tone="success" />
          <MetricCard label="Pending invitations" value={counts.pending} hint="Activation not completed" icon={Mail} tone="warning" />
          <MetricCard label="Disabled" value={counts.disabled} hint="Explicitly blocked by an administrator" icon={ShieldAlert} tone="danger" />
        </div>

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <SegmentedTabs
                tabs={FILTERS.map((item) => ({ ...item, badge: counts[item.id] }))}
                activeTab={filter}
                onChange={setFilter}
                ariaLabel="Administrator status filters"
              />
              <div className="w-full xl:max-w-md">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search name, email, role, or group"
                  ariaLabel="Search administrator accounts"
                />
              </div>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {visibleUsers.length ? (
              visibleUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUser(user)}
                  className="flex w-full flex-col gap-4 px-5 py-4 text-left transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-sm font-black text-orange-700 ring-1 ring-orange-100">
                      {(user.full_name || user.email || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-black text-slate-950">{user.full_name || 'Unnamed administrator'}</p>
                        {isMicrosoftLinked(user) && <StatusPill label="Microsoft linked" tone="info" />}
                      </div>
                      <p className="mt-1 truncate text-sm text-slate-500">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    {(user.groups || []).slice(0, 2).map((group) => (
                      <span key={group.id} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {group.name}
                      </span>
                    ))}
                    {(user.groups || []).length > 2 && (
                      <span className="text-xs font-semibold text-slate-400">+{user.groups.length - 2}</span>
                    )}
                    <StatusPill label={user.role || 'VIEWER'} tone="neutral" />
                    {stateBadge(user)}
                  </div>
                </button>
              ))
            ) : (
              <div className="p-6">
                <EmptyState
                  icon={Users}
                  title="No matching administrator accounts"
                  description="Adjust the status filter or search term."
                />
              </div>
            )}
          </div>
        </section>

        {selectedUser && (
          <div className="fixed inset-0 z-[60] flex justify-end bg-slate-950/35 backdrop-blur-sm">
            <button type="button" aria-label="Close administrator details" onClick={() => setSelectedUser(null)} className="flex-1" />
            <aside className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-600">Administrator detail</p>
                  <h2 className="mt-1 text-xl font-black text-slate-950">{selectedUser.full_name || 'Unnamed administrator'}</h2>
                  <p className="mt-1 text-sm text-slate-500">{selectedUser.email}</p>
                </div>
                <button type="button" onClick={() => setSelectedUser(null)} aria-label="Close administrator details panel" className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6 px-6 py-6">
                <div className="flex flex-wrap gap-2">
                  {stateBadge(selectedUser)}
                  <StatusPill label={selectedUser.role || 'VIEWER'} tone="neutral" />
                  <StatusPill label={isMicrosoftLinked(selectedUser) ? 'Microsoft linked' : 'Local authentication'} tone={isMicrosoftLinked(selectedUser) ? 'info' : 'brand'} />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Last login</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(selectedUser.last_login)}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Account created</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(selectedUser.created_at)}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-black text-slate-950">Effective groups</h3>
                  <div className="mt-3 space-y-2">
                    {(selectedUser.groups || []).length ? (
                      selectedUser.groups.map((group) => (
                        <div key={group.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3">
                          <KeyRound className="h-4 w-4 text-orange-600" />
                          <span className="text-sm font-bold text-slate-900">{group.name}</span>
                        </div>
                      ))
                    ) : (
                      <p className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">No local authorization group is assigned.</p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 p-5">
                  <h3 className="font-black text-slate-950">Account actions</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Status changes affect authentication. Group and permission changes belong in Groups & Permissions.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-3">
                    {userState(selectedUser) === 'pending' && !isMicrosoftLinked(selectedUser) && (
                      <button type="button" onClick={() => resendActivation(selectedUser)} className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm font-bold text-orange-700 hover:bg-orange-100">
                        <Mail className="h-4 w-4" />
                        Resend invitation
                      </button>
                    )}

                    {selectedUser.is_active ? (
                      <button
                        type="button"
                        onClick={() => setConfirmAction({ user: selectedUser, nextActive: false })}
                        className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-bold text-red-700 hover:bg-red-100"
                      >
                        Deactivate administrator
                      </button>
                    ) : userState(selectedUser) === 'disabled' ? (
                      <button
                        type="button"
                        onClick={() => setConfirmAction({ user: selectedUser, nextActive: true })}
                        className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
                      >
                        Reactivate administrator
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}

        <CreateAdminDialog
          open={showCreate}
          groups={groups}
          busy={creating}
          onClose={() => setShowCreate(false)}
          onCreate={createUser}
        />

        <ConfirmDialog
          open={Boolean(confirmAction)}
          title={confirmAction?.nextActive ? 'Reactivate administrator?' : 'Deactivate administrator?'}
          description={
            confirmAction?.nextActive
              ? `This will allow ${confirmAction?.user?.email || 'the administrator'} to authenticate again, subject to RBAC.`
              : `This will block ${confirmAction?.user?.email || 'the administrator'} from signing in.`
          }
          confirmLabel={confirmAction?.nextActive ? 'Reactivate account' : 'Deactivate account'}
          tone={confirmAction?.nextActive ? 'brand' : 'danger'}
          onClose={() => setConfirmAction(null)}
          onConfirm={updateStatus}
        >
          {!confirmAction?.nextActive && (
            <InlineAlert tone="danger" title="Authentication impact">
              The account will be blocked. Existing session revocation follows the backend account-status policy.
            </InlineAlert>
          )}
        </ConfirmDialog>
      </div>
    </PermissionGate>
  );
}
